/**
 * Merging the device's curated copy of a staple into a row the person already has (food staples
 * plan 4c, #5625, Requirement C4).
 *
 * TWO CALLERS, ONE RULE, and the second one only exists because the first one's mistake was made
 * twice: the offline food search and the scanners both end up with the person's own row for a staple
 * beside the device's curated copy of it, and both must resolve that the same way. The first version
 * of each replaced their document wholesale, which destroyed the `local` block — the like mark and
 * the last-used date — that the ranking weights by and that two of the Foods page's filters decide
 * by. Keeping this in one place is what stops a third copy getting it wrong again.
 */

import { Food, FoodHeader } from '../data-model/food';

/** what the caller must supply to rebuild the row from the merged document */
export interface StapleMergeDeps {
  /** turns a staple document into a row, exactly as a fresh one is built */
  buildRow: (staple: Food) => FoodHeader;
}

/**
 * Update the person's own row of a staple to the curated values, WITHOUT losing what makes it
 * theirs. Modifies `theirs` in place, because the caller already has it in a list and the ranking's
 * ordering depends on it staying the same object.
 *
 * WHY THEIR ROW IS THE ONE KEPT: it is the one that knows they eat this food. That knowledge is the
 * food's `local` block, and it is read off `cachedFood`, never off the header — `rankFoodHeaders`
 * takes the last-used date and the like mark from there, and the Foods page's Favorites and
 * Logged-Before filters decide by the same block. So the curated document's values are taken and
 * their `local` block is carried onto them.
 *
 * WHY THE ROW IS REBUILT: the header's own name and formatted numbers are what a list draws. Leaving
 * them built from their frozen copy shows the old name and the old numbers beside the corrected
 * document behind them.
 *
 * WHY THE CURATED DOCUMENT IS NOT USED DIRECTLY: it is the snapshot's own object, shared by every
 * other caller and kept by identity by the pre-analysed word map. Handing it out would let anything
 * that writes to a row's food reach into the device's copy of the staples.
 */
export function mergeStapleIntoTheirRow(theirs: FoodHeader, curated: Food | undefined, deps: StapleMergeDeps): void {
  // nothing better than their own copy to offer, so leave it exactly as it was
  if (!curated) {
    return;
  }
  const theirCopy = theirs.cachedFood as Food | undefined;
  const merged: Food = theirCopy?.local
    ? { ...curated, local: theirCopy.local }
    : { ...curated };
  const rebuilt = deps.buildRow(merged);
  theirs.name = rebuilt.name;
  theirs.status = rebuilt.status;
  theirs.displayNutrients = rebuilt.displayNutrients;
  theirs.recipe = rebuilt.recipe;
  theirs.cachedFood = merged;
  theirs.liked = merged.local?.liked;
}

/**
 * Split staples into the ones to add to a list and the ones the list already has, merging the
 * second kind into the rows that are already there.
 *
 * Device rows are not de-duplicated against each other by the ranking — it only drops a SERVER row
 * that a device row covers — so without this a staple the person has used before is weighed twice.
 */
export function stapleRowsToAdd(existing: FoodHeader[], staples: FoodHeader[], deps: StapleMergeDeps): FoodHeader[] {
  if (staples.length === 0) {
    return [];
  }
  const theirs = new Map<string, FoodHeader>();
  for (const row of existing) {
    if (row?._id) {
      theirs.set(row._id, row);
    }
  }
  const added: FoodHeader[] = [];
  for (const staple of staples) {
    const theirRow = theirs.get(staple._id);
    if (!theirRow) {
      added.push(staple);
      continue;
    }
    mergeStapleIntoTheirRow(theirRow, staple.cachedFood as Food | undefined, deps);
  }
  return added;
}
